import { ensureDefined } from "common/utils/assertOfType";

/**
 * Functions, types for 4x4 matrices that are commonly used for transforming 3d geometry
 * @module
 */
import { DIST_EPSILON } from "./core";
import { line2Tangent } from "./line2";
import type { Mat3 } from "./mat3";
import { ray2DotProject } from "./ray2";
import { vec2AngleBetween, vec2Dist } from "./vec2";
import type { Vec3, Vec4 } from "./vec3";
import { vec3Cross, vec3Dot, vec3Result, vec4Result } from "./vec3";

// Similar to three.js, our matrices are specified in column-major order.

export interface Mat4 {
  [position: number]: number;
}

export function mat4Copy(m: Mat4, dst: Mat4 = new Array<number>(16)): Mat4 {
  dst[0] = m[0];
  dst[1] = m[1];
  dst[2] = m[2];
  dst[3] = m[3];

  dst[4] = m[4];
  dst[5] = m[5];
  dst[6] = m[6];
  dst[7] = m[7];

  dst[8] = m[8];
  dst[9] = m[9];
  dst[10] = m[10];
  dst[11] = m[11];

  dst[12] = m[12];
  dst[13] = m[13];
  dst[14] = m[14];
  dst[15] = m[15];

  return dst;
}

export function mat4Ident(dst: Mat4 = new Array<number>(16)): Mat4 {
  dst[0] = 1;
  dst[1] = 0;
  dst[2] = 0;
  dst[3] = 0;

  dst[4] = 0;
  dst[5] = 1;
  dst[6] = 0;
  dst[7] = 0;

  dst[8] = 0;
  dst[9] = 0;
  dst[10] = 1;
  dst[11] = 0;

  dst[12] = 0;
  dst[13] = 0;
  dst[14] = 0;
  dst[15] = 1;

  return dst;
}

export function mat4Translate(x: number, y: number, z: number, dst: Mat4 = new Array<number>(16)): Mat4 {
  dst[0] = 1;
  dst[1] = 0;
  dst[2] = 0;
  dst[3] = 0;

  dst[4] = 0;
  dst[5] = 1;
  dst[6] = 0;
  dst[7] = 0;

  dst[8] = 0;
  dst[9] = 0;
  dst[10] = 1;
  dst[11] = 0;

  dst[12] = x;
  dst[13] = y;
  dst[14] = z;
  dst[15] = 1;

  return dst;
}

export function mat4Scale(x: number, y: number, z: number, dst: Mat4 = new Array<number>(16)): Mat4 {
  dst[0] = x;
  dst[1] = 0;
  dst[2] = 0;
  dst[3] = 0;

  dst[4] = 0;
  dst[5] = y;
  dst[6] = 0;
  dst[7] = 0;

  dst[8] = 0;
  dst[9] = 0;
  dst[10] = z;
  dst[11] = 0;

  dst[12] = 0;
  dst[13] = 0;
  dst[14] = 0;
  dst[15] = 1;

  return dst;
}

export function mat4ByBasisVectors(
  xAxisX: number,
  xAxisY: number,
  xAxisZ: number,
  yAxisX: number,
  yAxisY: number,
  yAxisZ: number,
  zAxisX: number,
  zAxisY: number,
  zAxisZ: number,
  dst: Mat4 = new Array<number>(16),
): Mat4 {
  dst[0] = xAxisX;
  dst[1] = xAxisY;
  dst[2] = xAxisZ;
  dst[3] = 0;

  dst[4] = yAxisX;
  dst[5] = yAxisY;
  dst[6] = yAxisZ;
  dst[7] = 0;

  dst[8] = zAxisX;
  dst[9] = zAxisY;
  dst[10] = zAxisZ;
  dst[11] = 0;

  dst[12] = 0;
  dst[13] = 0;
  dst[14] = 0;
  dst[15] = 1;

  return dst;
}

export function mat4Ortho(
  left: number,
  right: number,
  bottom: number,
  top: number,
  near: number,
  far: number,
  dst: Mat4 = new Array<number>(16),
): Mat4 {
  const lX = right - left;
  const lY = top - bottom;
  const lZ = far - near;

  dst[0] = 2 / lX;
  dst[1] = 0;
  dst[2] = 0;
  dst[3] = 0;

  dst[4] = 0;
  dst[5] = 2 / lY;
  dst[6] = 0;
  dst[7] = 0;

  dst[8] = 0;
  dst[9] = 0;
  dst[10] = 1 / lZ;
  dst[11] = 0;

  dst[12] = -(left + right) / lX;
  dst[13] = -(bottom + top) / lY;
  dst[14] = -near / lZ;
  dst[15] = 1;

  return dst;
}

export function mat4Perspective(
  aspect: number,
  fovDegrees: number,
  znear: number,
  zfar: number,
  dst: Mat4 = new Array<number>(16),
): Mat4 {
  const fovRads = fovDegrees * (Math.PI / 180);
  const b = 1.0 / Math.tan(fovRads * 0.5);
  const a = b / aspect;
  const c = zfar / (zfar - znear);
  const d = -(zfar * znear) / (zfar - znear);

  dst[0] = a;
  dst[1] = 0;
  dst[2] = 0;
  dst[3] = 0;

  dst[4] = 0;
  dst[5] = b;
  dst[6] = 0;
  dst[7] = 0;

  dst[8] = 0;
  dst[9] = 0;
  dst[10] = c;
  dst[11] = -1;

  dst[12] = 0;
  dst[13] = 0;
  dst[14] = d;
  dst[15] = 0;

  return dst;
}

/**
 * left, right, top, and bottom parameters represent the bounds of the frustum on the near plane. This can be used to create a cropped (sheared) perspective frustum.
 */
export function mat4PerspectiveCropped(
  left: number,
  right: number,
  top: number,
  bottom: number,
  znear: number,
  zfar: number,
  dst: Mat4 = new Array<number>(16),
): Mat4 {
  const x = (2 * znear) / (right - left);
  const y = (2 * znear) / (top - bottom);

  const a = (right + left) / (right - left);
  const b = (top + bottom) / (top - bottom);

  const c = zfar / (zfar - znear);
  const d = -(zfar * znear) / (zfar - znear);

  dst[0] = x;
  dst[1] = 0;
  dst[2] = 0;
  dst[3] = 0;

  dst[4] = 0;
  dst[5] = y;
  dst[6] = 0;
  dst[7] = 0;

  dst[8] = a;
  dst[9] = b;
  dst[10] = c;
  dst[11] = -1;

  dst[12] = 0;
  dst[13] = 0;
  dst[14] = d;
  dst[15] = 0;

  return dst;
}

export function mat4Invert(m: Mat4, dst: Mat4 = new Array<number>(16)): Mat4 {
  const m00 = m[0 * 4 + 0];
  const m01 = m[0 * 4 + 1];
  const m02 = m[0 * 4 + 2];
  const m03 = m[0 * 4 + 3];
  const m10 = m[1 * 4 + 0];
  const m11 = m[1 * 4 + 1];
  const m12 = m[1 * 4 + 2];
  const m13 = m[1 * 4 + 3];
  const m20 = m[2 * 4 + 0];
  const m21 = m[2 * 4 + 1];
  const m22 = m[2 * 4 + 2];
  const m23 = m[2 * 4 + 3];
  const m30 = m[3 * 4 + 0];
  const m31 = m[3 * 4 + 1];
  const m32 = m[3 * 4 + 2];
  const m33 = m[3 * 4 + 3];
  const tmp0 = m22 * m33;
  const tmp1 = m32 * m23;
  const tmp2 = m12 * m33;
  const tmp3 = m32 * m13;
  const tmp4 = m12 * m23;
  const tmp5 = m22 * m13;
  const tmp6 = m02 * m33;
  const tmp7 = m32 * m03;
  const tmp8 = m02 * m23;
  const tmp9 = m22 * m03;
  const tmp10 = m02 * m13;
  const tmp11 = m12 * m03;
  const tmp12 = m20 * m31;
  const tmp13 = m30 * m21;
  const tmp14 = m10 * m31;
  const tmp15 = m30 * m11;
  const tmp16 = m10 * m21;
  const tmp17 = m20 * m11;
  const tmp18 = m00 * m31;
  const tmp19 = m30 * m01;
  const tmp20 = m00 * m21;
  const tmp21 = m20 * m01;
  const tmp22 = m00 * m11;
  const tmp23 = m10 * m01;

  const t0 = tmp0 * m11 + tmp3 * m21 + tmp4 * m31 - (tmp1 * m11 + tmp2 * m21 + tmp5 * m31);
  const t1 = tmp1 * m01 + tmp6 * m21 + tmp9 * m31 - (tmp0 * m01 + tmp7 * m21 + tmp8 * m31);
  const t2 = tmp2 * m01 + tmp7 * m11 + tmp10 * m31 - (tmp3 * m01 + tmp6 * m11 + tmp11 * m31);
  const t3 = tmp5 * m01 + tmp8 * m11 + tmp11 * m21 - (tmp4 * m01 + tmp9 * m11 + tmp10 * m21);

  const d = 1.0 / (m00 * t0 + m10 * t1 + m20 * t2 + m30 * t3);

  dst[0] = d * t0;
  dst[1] = d * t1;
  dst[2] = d * t2;
  dst[3] = d * t3;
  dst[4] = d * (tmp1 * m10 + tmp2 * m20 + tmp5 * m30 - (tmp0 * m10 + tmp3 * m20 + tmp4 * m30));
  dst[5] = d * (tmp0 * m00 + tmp7 * m20 + tmp8 * m30 - (tmp1 * m00 + tmp6 * m20 + tmp9 * m30));
  dst[6] = d * (tmp3 * m00 + tmp6 * m10 + tmp11 * m30 - (tmp2 * m00 + tmp7 * m10 + tmp10 * m30));
  dst[7] = d * (tmp4 * m00 + tmp9 * m10 + tmp10 * m20 - (tmp5 * m00 + tmp8 * m10 + tmp11 * m20));
  dst[8] = d * (tmp12 * m13 + tmp15 * m23 + tmp16 * m33 - (tmp13 * m13 + tmp14 * m23 + tmp17 * m33));
  dst[9] = d * (tmp13 * m03 + tmp18 * m23 + tmp21 * m33 - (tmp12 * m03 + tmp19 * m23 + tmp20 * m33));
  dst[10] = d * (tmp14 * m03 + tmp19 * m13 + tmp22 * m33 - (tmp15 * m03 + tmp18 * m13 + tmp23 * m33));
  dst[11] = d * (tmp17 * m03 + tmp20 * m13 + tmp23 * m23 - (tmp16 * m03 + tmp21 * m13 + tmp22 * m23));
  dst[12] = d * (tmp14 * m22 + tmp17 * m32 + tmp13 * m12 - (tmp16 * m32 + tmp12 * m12 + tmp15 * m22));
  dst[13] = d * (tmp20 * m32 + tmp12 * m02 + tmp19 * m22 - (tmp18 * m22 + tmp21 * m32 + tmp13 * m02));
  dst[14] = d * (tmp18 * m12 + tmp23 * m32 + tmp15 * m02 - (tmp22 * m32 + tmp14 * m02 + tmp19 * m12));
  dst[15] = d * (tmp22 * m22 + tmp16 * m02 + tmp21 * m12 - (tmp20 * m12 + tmp23 * m22 + tmp17 * m02));

  return dst;
}

export function mat4Transpose(a: Mat4, out: Mat4 = new Array<number>(16)) {
  // If we are transposing ourselves we can skip a few steps but have to cache some values
  if (out === a) {
    const a01 = a[1];
    const a02 = a[2];
    const a03 = a[3];
    const a12 = a[6];
    const a13 = a[7];
    const a23 = a[11];

    out[1] = a[4];
    out[2] = a[8];
    out[3] = a[12];
    out[4] = a01;
    out[6] = a[9];
    out[7] = a[13];
    out[8] = a02;
    out[9] = a12;
    out[11] = a[14];
    out[12] = a03;
    out[13] = a13;
    out[14] = a23;
  } else {
    out[0] = a[0];
    out[1] = a[4];
    out[2] = a[8];
    out[3] = a[12];
    out[4] = a[1];
    out[5] = a[5];
    out[6] = a[9];
    out[7] = a[13];
    out[8] = a[2];
    out[9] = a[6];
    out[10] = a[10];
    out[11] = a[14];
    out[12] = a[3];
    out[13] = a[7];
    out[14] = a[11];
    out[15] = a[15];
  }

  return out;
}

export function mat4Mult(a: Mat4, b: Mat4, dst: Mat4 = new Array<number>(16)): Mat4 {
  const a00 = a[0];
  const a01 = a[1];
  const a02 = a[2];
  const a03 = a[3];
  const a10 = a[4 + 0];
  const a11 = a[4 + 1];
  const a12 = a[4 + 2];
  const a13 = a[4 + 3];
  const a20 = a[8 + 0];
  const a21 = a[8 + 1];
  const a22 = a[8 + 2];
  const a23 = a[8 + 3];
  const a30 = a[12 + 0];
  const a31 = a[12 + 1];
  const a32 = a[12 + 2];
  const a33 = a[12 + 3];
  const b00 = b[0];
  const b01 = b[1];
  const b02 = b[2];
  const b03 = b[3];
  const b10 = b[4 + 0];
  const b11 = b[4 + 1];
  const b12 = b[4 + 2];
  const b13 = b[4 + 3];
  const b20 = b[8 + 0];
  const b21 = b[8 + 1];
  const b22 = b[8 + 2];
  const b23 = b[8 + 3];
  const b30 = b[12 + 0];
  const b31 = b[12 + 1];
  const b32 = b[12 + 2];
  const b33 = b[12 + 3];

  dst[0] = a00 * b00 + a10 * b01 + a20 * b02 + a30 * b03;
  dst[1] = a01 * b00 + a11 * b01 + a21 * b02 + a31 * b03;
  dst[2] = a02 * b00 + a12 * b01 + a22 * b02 + a32 * b03;
  dst[3] = a03 * b00 + a13 * b01 + a23 * b02 + a33 * b03;
  dst[4] = a00 * b10 + a10 * b11 + a20 * b12 + a30 * b13;
  dst[5] = a01 * b10 + a11 * b11 + a21 * b12 + a31 * b13;
  dst[6] = a02 * b10 + a12 * b11 + a22 * b12 + a32 * b13;
  dst[7] = a03 * b10 + a13 * b11 + a23 * b12 + a33 * b13;
  dst[8] = a00 * b20 + a10 * b21 + a20 * b22 + a30 * b23;
  dst[9] = a01 * b20 + a11 * b21 + a21 * b22 + a31 * b23;
  dst[10] = a02 * b20 + a12 * b21 + a22 * b22 + a32 * b23;
  dst[11] = a03 * b20 + a13 * b21 + a23 * b22 + a33 * b23;
  dst[12] = a00 * b30 + a10 * b31 + a20 * b32 + a30 * b33;
  dst[13] = a01 * b30 + a11 * b31 + a21 * b32 + a31 * b33;
  dst[14] = a02 * b30 + a12 * b31 + a22 * b32 + a32 * b33;
  dst[15] = a03 * b30 + a13 * b31 + a23 * b32 + a33 * b33;

  return dst;
}

export function mat4DotExt(m: Mat4, x: number, y: number, z: number): Vec4 {
  const dst = vec4Result;

  dst[0] = x * m[0] + y * m[4] + z * m[8] + m[12];
  dst[1] = x * m[1] + y * m[5] + z * m[9] + m[13];
  dst[2] = x * m[2] + y * m[6] + z * m[10] + m[14];
  dst[3] = x * m[3] + y * m[7] + z * m[11] + m[15];

  return dst;
}

export function mat4DotSub3(m: Mat4, x: number, y: number, z: number): Vec3 {
  const dst = vec3Result;

  dst[0] = x * m[0] + y * m[4] + z * m[8];
  dst[1] = x * m[1] + y * m[5] + z * m[9];
  dst[2] = x * m[2] + y * m[6] + z * m[10];

  return dst;
}

export function mat4LinearDeterminant3(m: Mat4): number {
  const m0 = m[0];
  const m1 = m[1];
  const m2 = m[2];
  const m4 = m[4];
  const m5 = m[5];
  const m6 = m[6];
  const m8 = m[8];
  const m9 = m[9];
  const m10 = m[10];
  return m0 * (m5 * m10 - m6 * m9) - m1 * (m4 * m10 - m6 * m8) + m2 * (m4 * m9 - m5 * m8);
}

/** Returns true if the rotation/scaling part of the transform mirrors geometry. */
export function mat4IsMirroring(m: Mat4): boolean {
  return mat4LinearDeterminant3(m) < 0;
}

export function mat4Dot(m: Mat4, x: number, y: number, z: number): Vec3 {
  const dst = vec3Result;

  const d = x * m[3] + y * m[7] + z * m[11] + m[15];

  dst[0] = (x * m[0] + y * m[4] + z * m[8] + m[12]) / d;
  dst[1] = (x * m[1] + y * m[5] + z * m[9] + m[13]) / d;
  dst[2] = (x * m[2] + y * m[6] + z * m[10] + m[14]) / d;

  return dst;
}

export function mat4Rotate(
  radians: number,
  x: number,
  y: number,
  z: number,
  out: Mat4 = new Array<number>(16),
): Mat4 | null {
  let len = Math.hypot(x, y, z);
  if (len < 1e-13) {
    return null;
  }

  len = 1 / len;
  x *= len;
  y *= len;
  z *= len;
  const s = Math.sin(radians);
  const c = Math.cos(radians);
  const t = 1 - c;

  // Perform rotation-specific matrix multiplication
  out[0] = x * x * t + c;
  out[1] = y * x * t + z * s;
  out[2] = z * x * t - y * s;
  out[3] = 0;
  out[4] = x * y * t - z * s;
  out[5] = y * y * t + c;
  out[6] = z * y * t + x * s;
  out[7] = 0;
  out[8] = x * z * t + y * s;
  out[9] = y * z * t - x * s;
  out[10] = z * z * t + c;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;

  return out;
}

export function mat4LookAt(eye: Vec3, center: Vec3, up: Vec3, dst: Mat4 = new Array<number>(16)) {
  let x0: number,
    x1: number,
    x2: number,
    y0: number,
    y1: number,
    y2: number,
    z0: number,
    z1: number,
    z2: number,
    len: number;
  const eyex = eye[0];
  const eyey = eye[1];
  const eyez = eye[2];
  const upx = up[0];
  const upy = up[1];
  const upz = up[2];
  const centerx = center[0];
  const centery = center[1];
  const centerz = center[2];

  if (Math.abs(eyex - centerx) < 1e-6 && Math.abs(eyey - centery) < 1e-6 && Math.abs(eyez - centerz) < 1e-6) {
    return mat4Ident(dst);
  }

  z0 = eyex - centerx;
  z1 = eyey - centery;
  z2 = eyez - centerz;

  len = 1 / Math.hypot(z0, z1, z2);
  z0 *= len;
  z1 *= len;
  z2 *= len;

  x0 = upy * z2 - upz * z1;
  x1 = upz * z0 - upx * z2;
  x2 = upx * z1 - upy * z0;
  len = Math.hypot(x0, x1, x2);
  if (!len) {
    x0 = 0;
    x1 = 0;
    x2 = 0;
  } else {
    len = 1 / len;
    x0 *= len;
    x1 *= len;
    x2 *= len;
  }

  y0 = z1 * x2 - z2 * x1;
  y1 = z2 * x0 - z0 * x2;
  y2 = z0 * x1 - z1 * x0;

  len = Math.hypot(y0, y1, y2);
  if (!len) {
    y0 = 0;
    y1 = 0;
    y2 = 0;
  } else {
    len = 1 / len;
    y0 *= len;
    y1 *= len;
    y2 *= len;
  }

  dst[0] = x0;
  dst[1] = y0;
  dst[2] = z0;
  dst[3] = 0;
  dst[4] = x1;
  dst[5] = y1;
  dst[6] = z1;
  dst[7] = 0;
  dst[8] = x2;
  dst[9] = y2;
  dst[10] = z2;
  dst[11] = 0;
  dst[12] = -(x0 * eyex + x1 * eyey + x2 * eyez);
  dst[13] = -(y0 * eyex + y1 * eyey + y2 * eyez);
  dst[14] = -(z0 * eyex + z1 * eyey + z2 * eyez);
  dst[15] = 1;

  return dst;
}

/**
 * Return the view direction from a look-at matrix.
 */
export function mat4GetViewDirection(mat4: Mat4): Vec3 {
  vec3Result[0] = -mat4[2];
  vec3Result[1] = -mat4[6];
  vec3Result[2] = -mat4[10];
  return vec3Result;
}

// Returns a matrix M such that: mat4Dot(M, srcX, srcY, srcZ) === Vec3(dstX, dstY, dstZ)
//
// Note: src and dst are assumed to be unit vectors
export function mat4RotateVector(
  srcX: number,
  srcY: number,
  srcZ: number,
  dstX: number,
  dstY: number,
  dstZ: number,
  out: Mat4 = new Array<number>(16),
): Mat4 {
  const c = vec3Dot(srcX, srcY, srcZ, dstX, dstY, dstZ);
  const [vX, vY, vZ] = vec3Cross(srcX, srcY, srcZ, dstX, dstY, dstZ);

  if (vec3Dot(vX, vY, vZ, vX, vY, vZ) < DIST_EPSILON) {
    if (c > 0) {
      return mat4Ident(out);
    } else {
      return mat4Scale(-1, -1, -1, out);
    }
  } else {
    // Formula: https://www.theochem.ru.nl/~pwormer/Knowino/knowino.org/wiki/Rotation_matrix.html#Vector_rotation
    const h = 1.0 / (1.0 + c);

    out[0] = vX * vX * h + c;
    out[1] = vX * vY * h + vZ;
    out[2] = vX * vZ * h - vY;
    out[3] = 0;

    out[4] = vY * vX * h - vZ;
    out[5] = vY * vY * h + c;
    out[6] = vY * vZ * h + vX;
    out[7] = 0;

    out[8] = vZ * vX * h + vY;
    out[9] = vZ * vY * h - vX;
    out[10] = vZ * vZ * h + c;
    out[11] = 0;

    out[12] = 0;
    out[13] = 0;
    out[14] = 0;
    out[15] = 1;
  }

  return out;
}

export function mat4RotateByCenterStartEndPoint(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): Mat4 {
  const [tx0, ty0] = line2Tangent(ax, ay, bx, by);
  const [tx1, ty1] = line2Tangent(ax, ay, cx, cy);

  const radians = vec2AngleBetween(tx0, ty0, tx1, ty1);
  const translate1 = mat4Translate(ax, ay, 0);
  const rotate1 = ensureDefined(mat4Rotate(radians, 0, 0, 1));
  const translate2 = mat4Translate(-ax, -ay, 0);

  return mat4Mult(translate1, mat4Mult(rotate1, translate2));
}

export function mat4RotatePivotAxis(
  pivotX: number,
  pivotY: number,
  pivotZ: number,
  radians: number,
  axisX: number,
  axisY: number,
  axisZ: number,
): Mat4 | null {
  const rotate1 = mat4Rotate(radians, axisX, axisY, axisZ);
  if (!rotate1) {
    return null;
  }
  const translate1 = mat4Translate(pivotX, pivotY, pivotZ);
  const translate2 = mat4Translate(-pivotX, -pivotY, -pivotZ);

  return mat4Mult(translate1, mat4Mult(rotate1, translate2));
}

/**
 * Converts a Mat3 (2D transformation) to Mat4 (3D transformation).
 * The transformation leaves the Z axis unchanged.
 */
export function mat3ToMat4(mat: Mat3, dst: Mat4 = new Array<number>(16)): Mat4 {
  dst[0] = mat[0];
  dst[1] = mat[1];
  dst[2] = 0;
  dst[3] = mat[2];

  dst[4] = mat[3];
  dst[5] = mat[4];
  dst[6] = 0;
  dst[7] = mat[5];

  dst[8] = 0;
  dst[9] = 0;
  dst[10] = 1;
  dst[11] = 0;

  dst[12] = mat[6];
  dst[13] = mat[7];
  dst[14] = 0;
  dst[15] = mat[8];

  return dst;
}

export function mat4StretchByThreePoints(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  tolerance: number = DIST_EPSILON,
): Mat4 {
  const d0 = vec2Dist(ax, ay, bx, by);

  if (d0 < tolerance) {
    return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  }

  const [tx, ty] = line2Tangent(ax, ay, bx, by);

  const d1 = ray2DotProject(ax, ay, tx, ty, cx, cy);

  const scale = d1 / d0;

  const radians = vec2AngleBetween(1, 0, tx, ty);
  const translate1 = mat4Translate(ax, ay, 0);
  const rotate1 = ensureDefined(mat4Rotate(radians, 0, 0, 1));
  const scaling = mat4Scale(scale, 1, 1);
  const rotate2 = ensureDefined(mat4Rotate(-radians, 0, 0, 1));
  const translate2 = mat4Translate(-ax, -ay, 0);

  return mat4Mult(translate1, mat4Mult(rotate1, mat4Mult(scaling, mat4Mult(rotate2, translate2))));
}

export function mat4ScaleByThreePoints(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  tolerance: number = DIST_EPSILON,
): Mat4 {
  const d0 = vec2Dist(ax, ay, bx, by);

  if (d0 < tolerance) {
    return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  }

  const [tx, ty] = line2Tangent(ax, ay, bx, by);

  const d1 = ray2DotProject(ax, ay, tx, ty, cx, cy);

  const scale = d1 / d0;

  const translate1 = mat4Translate(ax, ay, 0);
  const scaling = mat4Scale(scale, scale, 1);
  const translate2 = mat4Translate(-ax, -ay, 0);

  return mat4Mult(translate1, mat4Mult(scaling, translate2));
}

export function mat4IsNear(a: Mat4, b: Mat4, tolerance: number): boolean {
  for (let i = 0; i < 16; ++i) {
    if (Math.abs(a[i] - b[i]) > tolerance) {
      return false;
    }
  }
  return true;
}

/**
 * Transform x/y values from normalized device coordinates (NDC) to a viewport.
 * NDC x/y values range from -1 to 1.
 * Viewport ranges from 0 to width/height.
 * @param width Viewport width
 * @param height Viewport height
 * @param depth Optional scale for z values. If left undefined, the z values are flattened.
 * @returns Transformation matrix
 */
export function ndcToViewportMatrix(width: number, height: number, depth: number = 0) {
  const viewPortTranslateMatrix = mat4Translate(1.0, 1.0, 0.0); // Translate from NDC coordinate space [-1, 1] to [0, 2]
  const viewPortScaleMatrix = mat4Scale(width * 0.5, height * 0.5, depth); // width * 0.5 multiplier to get us from [0, 2] to [0, width]
  return mat4Mult(viewPortScaleMatrix, viewPortTranslateMatrix);
}
